/**
 * M12 — Histórico de Inspeções Regulares (FIR).
 *
 * Réplica frontend-only do módulo M12 do protótipo V5 (docs/reference/
 * GeoGuard_UPM_v5_4_4.html, renderM12). Histórico de Fichas de Inspeção
 * Regular quinzenais: gráfico do Estado de Conservação (EC) por inspeção,
 * tabela das últimas 10 FIRs e anomalias mapeadas (expansíveis).
 *
 * POC de visualização para reunião — dados mockados inline (RISR 1S/2026
 * Tabela 4.1), sem datasource/Supabase. Quando integrar (ficha de inspeção
 * digital padrão JFB), trocar MOCK_FIR por um DataSource.
 */
import { useMemo, useState } from 'react';
import {
  Bar, BarChart, Cell, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardTitle } from '@/ui';

interface Anomalia {
  cat: string;
  desc: string;
  local: string;
  foto: string | null;
}

interface Fir {
  data: string;
  extrav: number;
  percolacao: number;
  deform: number;
  taludes: number;
  drenagem: number;
  /** Estado de Conservação máximo (ANM Res. 95/2022 Anexo IV). */
  ec: number;
  resp: string;
  status?: 'validado' | 'ia-confirmado';
  anomalia?: Anomalia | null;
  obs?: string;
}

/** FIR de rotina sem anomalia (estrutura estável). */
const ok = (data: string, resp = 'Equipe UPM'): Fir => ({
  data, extrav: 0, percolacao: 0, deform: 0, taludes: 0, drenagem: 0, ec: 0, resp,
});

// FIRs quinzenais — RISR 1S/2026 Tabela 4.1 (protótipo V5).
const MOCK_FIR: Fir[] = [
  ok('11/01/2023'), ok('25/01/2023'), ok('10/02/2023'), ok('27/02/2023'), ok('06/03/2023'),
  ok('16/03/2023'), ok('04/04/2023'), ok('24/04/2023'), ok('09/05/2023'), ok('30/05/2023'),
  ok('13/06/2023'), ok('20/06/2023'), ok('05/07/2023'), ok('18/08/2023'), ok('23/08/2023'),
  ok('13/09/2023'), ok('19/09/2023'), ok('04/10/2023'), ok('20/10/2023'), ok('10/11/2023'),
  ok('24/11/2023'), ok('06/12/2023'), ok('18/12/2023'), ok('10/01/2024'), ok('18/01/2024'),
  ok('16/02/2024'), ok('22/02/2024'), ok('15/03/2024'), ok('21/03/2024'), ok('03/04/2024'),
  ok('26/04/2024'), ok('07/05/2024'), ok('29/05/2024'), ok('12/06/2024'), ok('18/06/2024'),
  ok('05/07/2024'), ok('18/07/2024'), ok('14/08/2024'), ok('30/08/2024'), ok('11/09/2024'),
  ok('26/09/2024'), ok('01/11/2024'), ok('08/11/2024'), ok('22/11/2024'), ok('13/12/2024'),
  ok('16/12/2024'), ok('08/01/2025'), ok('16/01/2025'), ok('05/02/2025'), ok('25/02/2025'),
  ok('13/03/2025'),
  {
    data: '31/03/2025', extrav: 0, percolacao: 0, deform: 2, taludes: 0, drenagem: 0, ec: 2, resp: 'Equipe UPM',
    anomalia: {
      cat: 'Deformações e Recalques',
      desc: 'Deformação observada na berma 798m, área próxima ao MSE-03. Sem evolução crítica. Monitoramento intensificado.',
      local: "Berma cota 798m — Seção C-C'",
      foto: null,
    },
  },
  ok('08/04/2025'), ok('25/04/2025'), ok('14/05/2025'), ok('27/05/2025'), ok('12/06/2025'),
  ok('18/06/2025'), ok('14/07/2025'), ok('24/07/2025'), ok('13/08/2025'), ok('21/08/2025'),
  ok('03/09/2025'), ok('22/09/2025'), ok('08/10/2025'), ok('23/10/2025'), ok('13/11/2025'),
  ok('27/11/2025'), ok('10/12/2025'),
  { ...ok('22/12/2025', 'Thiago B. G. Gomes'), status: 'validado' },
  {
    ...ok('03/02/2026', 'JF Brasil / Patos de Minas Fosfatados'),
    anomalia: null,
    obs: 'Inspeção de campo para RISR 1S/2026 — estrutura estável',
  },
];

/** Cor do EC — verde normal, amarelo atenção, laranja alerta, vermelho emergência. */
function ecColor(ec: number): string {
  if (ec === 0) return '#22c55e';
  if (ec <= 2) return '#eab308';
  if (ec <= 6) return '#f97316';
  return '#ef4444';
}

function AnomaliaCard({ fir }: { fir: Fir }) {
  const [open, setOpen] = useState(false);
  const a = fir.anomalia!;
  return (
    <div
      className="bg-jf-surface border rounded-xl p-4 mb-2"
      style={{ borderColor: 'rgba(234,179,8,.4)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-2.5 text-left"
        aria-expanded={open}
      >
        <div className="flex-1">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded border text-level-atencao border-level-atencao/30 bg-level-atencao/10">
              {a.cat}
            </span>
            <span className="text-[10px] text-jf-muted px-1.5 py-0.5">EC={fir.ec}</span>
          </div>
          <div className="text-sm font-semibold text-white">{a.desc}</div>
          <div className="text-[11px] text-jf-dim mt-1 flex flex-wrap gap-3">
            <span>📅 {fir.data}</span>
            <span>📍 {a.local}</span>
            <span>👤 {fir.resp}</span>
          </div>
        </div>
        <span className="text-jf-dim text-lg shrink-0">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t border-jf-border">
          {a.foto ? (
            <img src={a.foto} alt={a.desc} className="w-full max-h-48 object-cover rounded-lg mb-2.5" />
          ) : (
            <div className="border border-dashed border-jf-border rounded-lg h-28 flex items-center justify-center mb-2.5 bg-jf-bg">
              <span className="text-jf-dim text-xs">📷 Foto não disponível no sistema</span>
            </div>
          )}
          <p className="text-xs text-jf-muted">
            <strong className="text-white">Localização:</strong> {a.local}
          </p>
          <p className="text-xs text-jf-muted mt-1">
            <strong className="text-white">Responsável:</strong> {fir.resp}
          </p>
          {fir.obs && (
            <p className="text-xs text-jf-muted mt-1">
              <strong className="text-white">Obs.:</strong> {fir.obs}
            </p>
          )}
          <p className="text-[11px] text-jf-dim mt-2">
            Fonte: RISR 1S/2026 Tabela 4.1 — Barragem B, Patos de Minas Fosfatados
          </p>
        </div>
      )}
    </div>
  );
}

export default function M12Historico() {
  const anomalias = useMemo(() => MOCK_FIR.filter((f) => f.anomalia), []);
  const lastDate = MOCK_FIR[MOCK_FIR.length - 1]?.data ?? '—';
  const chartData = useMemo(() => MOCK_FIR.map((f) => ({ data: f.data, ec: f.ec })), []);
  const lastTen = useMemo(() => MOCK_FIR.slice(-10).reverse(), []);

  return (
    <div>
      <header className="mb-3">
        <h1 className="text-2xl font-black text-jf-yellow">📅 Histórico de Inspeções Regulares (FIR)</h1>
      </header>

      <div className="rounded-lg border border-jf-yellow/30 bg-jf-yellow/10 text-jf-yellow text-xs px-3 py-2 mb-3">
        {MOCK_FIR.length} FIRs quinzenais · Jan/2023 – Fev/2026 · Última: {lastDate} · Fonte: RISR 1S/2026 Tabela 4.1
      </div>

      <Card className="mb-3">
        <p className="text-xs text-jf-muted mb-2.5">
          Estado de Conservação (EC) por inspeção — Resolução ANM 95/2022 Anexo IV
        </p>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1a2c4a" vertical={false} />
              <XAxis dataKey="data" stroke="#94a3b8" tick={{ fontSize: 9 }} interval={5} angle={-45} textAnchor="end" height={50} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} width={32} domain={[0, 4]} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#1a2540', border: '1px solid #28334A', borderRadius: 8 }}
                labelStyle={{ color: '#94a3b8' }}
                itemStyle={{ color: '#fff' }}
              />
              <ReferenceLine y={2} stroke="#eab30855" strokeDasharray="4 4" label={{ value: 'Atenção', fill: '#eab308', fontSize: 9, position: 'insideTopRight' }} />
              <Bar dataKey="ec" name="EC máx" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {chartData.map((d) => (
                  <Cell key={d.data} fill={ecColor(d.ec)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-3 mt-2.5 text-[11px]">
          <span className="text-level-normal">● EC=0 Normal</span>
          <span className="text-level-atencao">● EC=2–3 Atenção</span>
          <span className="text-level-alerta">● EC=6 Nível de Alerta (2 ISR seguidas)</span>
          <span className="text-level-emergencia">● EC=10 Emergência (NE-1)</span>
        </div>
      </Card>

      <Card className="mb-3">
        <CardTitle>Pontuação por categoria — últimas 10 FIRs</CardTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-jf-dim text-[10px] uppercase tracking-wide">
                <th className="py-2 pr-3 text-left font-semibold">Data</th>
                <th className="py-2 px-2 font-semibold">Extrav.</th>
                <th className="py-2 px-2 font-semibold">Percol.</th>
                <th className="py-2 px-2 font-semibold">Deform.</th>
                <th className="py-2 px-2 font-semibold">Taludes</th>
                <th className="py-2 px-2 font-semibold">Drenagem</th>
                <th className="py-2 px-2 font-semibold">EC máx</th>
                <th className="py-2 px-2 text-left font-semibold">Responsável</th>
                <th className="py-2 pl-2 text-left font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {lastTen.map((f) => {
                const cell = (v: number) => (v > 0 ? 'text-level-atencao font-bold' : 'text-jf-muted');
                return (
                  <tr key={f.data} className="border-t border-jf-border">
                    <td className="py-2 pr-3 font-mono text-jf-muted">{f.data}</td>
                    <td className={`py-2 px-2 text-center ${cell(f.extrav)}`}>{f.extrav}</td>
                    <td className={`py-2 px-2 text-center ${cell(f.percolacao)}`}>{f.percolacao}</td>
                    <td className={`py-2 px-2 text-center ${cell(f.deform)}`}>{f.deform}</td>
                    <td className={`py-2 px-2 text-center ${cell(f.taludes)}`}>{f.taludes}</td>
                    <td className={`py-2 px-2 text-center ${cell(f.drenagem)}`}>{f.drenagem}</td>
                    <td className="py-2 px-2 text-center font-bold" style={{ color: ecColor(f.ec) }}>{f.ec}</td>
                    <td className="py-2 px-2 text-[11px] text-jf-muted">{f.resp}</td>
                    <td className="py-2 pl-2">
                      {f.status === 'validado' ? (
                        <span className="text-[10px] font-bold text-level-normal">✅ Validado</span>
                      ) : f.status === 'ia-confirmado' ? (
                        <span className="text-[10px] font-bold text-level-normal">🤖 IA+Téc.</span>
                      ) : (
                        <span className="text-[10px] text-jf-dim">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div>
        <CardTitle className="text-level-emergencia">🔴 Anomalias Mapeadas ({anomalias.length})</CardTitle>
        {anomalias.length === 0 ? (
          <p className="text-level-normal text-center py-4">✅ Nenhuma anomalia registrada no período avaliado.</p>
        ) : (
          anomalias.map((f) => <AnomaliaCard key={f.data} fir={f} />)
        )}
      </div>
    </div>
  );
}
